import { AMBIGUOUS_DOLLAR_CURRENCIES } from '@commutifi-fe/constants';

/**
 * Picks `currencyDisplay` for `Intl.NumberFormat` so a `$`-family currency
 * renders unambiguously for the viewer's locale.
 *
 * Intl already disambiguates most locale/currency pairs (en-CA + USD →
 * "US$50.00", fr-CA + USD → "50,00 $ US"). The hole is locales like de-DE
 * where Intl falls back to a bare "$" for foreign dollars — a German viewer
 * could read that as any dollar. In English locales the bare "$" is read as
 * USD by convention (en-US, en) so we leave it alone; everywhere else, fall
 * back to the ISO code ("50,00 USD") rather than show a misleading symbol.
 *
 * Scoped to dollar-family currencies — non-`$` currencies (EUR, GBP, …) are
 * unambiguous in every locale and bypass the override.
 */
export function pickDollarCurrencyDisplay(locale: string, currency: string): 'symbol' | 'code' {
  if (!AMBIGUOUS_DOLLAR_CURRENCIES.has(currency)) return 'symbol';
  return isEnglishLocale(locale) ? 'symbol' : 'code';
}
function isEnglishLocale(locale: string) {
  try {
    return new Intl.Locale(locale).language === 'en';
  } catch {
    return locale.toLowerCase().split(/[-_]/)[0] === 'en';
  }
}